import type { IntegrationProviderId } from '../types/integration'

export type IntegrationTier = { maxActiveConnections: number; enabledProviders: IntegrationProviderId[] }
export type IntegrationTierConfig = {
  flags: Partial<Record<IntegrationProviderId, boolean>>
  tiers: { free: IntegrationTier; paid: IntegrationTier }
  apiAccess: { enabledForTiers: Array<'free' | 'paid'> }
}

const ids: IntegrationProviderId[] = ['G_DRIVE', 'NOTION', 'JIRA', 'GOOGLE_CALENDAR', 'GOOGLE_SHEETS', 'SLACK', 'GENERIC_WEBHOOK', 'STAYS_PMS']

export const DEFAULT_INTEGRATION_TIER_CONFIG: IntegrationTierConfig = {
  flags: { G_DRIVE: false, NOTION: true, JIRA: true, GOOGLE_CALENDAR: true, GOOGLE_SHEETS: true, SLACK: true, GENERIC_WEBHOOK: false, STAYS_PMS: true },
  tiers: {
    free: { maxActiveConnections: 3, enabledProviders: ['NOTION', 'JIRA', 'GOOGLE_CALENDAR', 'GOOGLE_SHEETS', 'SLACK'] },
    paid: { maxActiveConnections: 20, enabledProviders: ['NOTION', 'JIRA', 'GOOGLE_CALENDAR', 'GOOGLE_SHEETS', 'SLACK'] },
  },
  apiAccess: { enabledForTiers: ['paid'] },
}

const rec = (value: unknown): Record<string, unknown> => value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}
const validProviders = (value: unknown, fallback: IntegrationProviderId[]) => Array.isArray(value) ? value.filter((item): item is IntegrationProviderId => typeof item === 'string' && ids.includes(item as IntegrationProviderId)) : fallback

export const normalizeIntegrationTierConfig = (value: unknown): IntegrationTierConfig => {
  const source = rec(value); const flags = rec(source.flags); const tiers = rec(source.tiers)
  const tier = (key: 'free' | 'paid') => {
    const raw = rec(tiers[key]); const fallback = DEFAULT_INTEGRATION_TIER_CONFIG.tiers[key]
    return { maxActiveConnections: Number.isSafeInteger(raw.maxActiveConnections) ? Math.max(0, Number(raw.maxActiveConnections)) : fallback.maxActiveConnections, enabledProviders: validProviders(raw.enabledProviders, fallback.enabledProviders) }
  }
  const access = rec(source.apiAccess).enabledForTiers
  return {
    flags: ids.reduce((out, id) => ({ ...out, [id]: typeof flags[id] === 'boolean' ? flags[id] : DEFAULT_INTEGRATION_TIER_CONFIG.flags[id] }), {} as Record<IntegrationProviderId, boolean>),
    tiers: { free: tier('free'), paid: tier('paid') },
    apiAccess: { enabledForTiers: Array.isArray(access) ? access.filter((item): item is 'free' | 'paid' => item === 'free' || item === 'paid') : DEFAULT_INTEGRATION_TIER_CONFIG.apiAccess.enabledForTiers },
  }
}
